import { create } from 'zustand';
import { Node, Edge, Connection, addEdge, applyNodeChanges, applyEdgeChanges, NodeChange, EdgeChange } from '@xyflow/react';
import { Workflow, NODE_TYPES } from './types';
import { DATA_ZONE_ID, DATA_ZONE_POSITION, DATA_NODE_TYPES, ZONE_SLOTS } from './DataZoneNode';

interface HistorySnapshot {
  nodes: Node[];
  edges: Edge[];
  workflowName: string;
  workflowDescription: string;
}

interface WorkflowStore {
  // Canvas state
  nodes: Node[];
  edges: Edge[];
  selectedNodeId: string | null;
  
  // Workflow metadata
  workflowName: string;
  workflowDescription: string;
  // Run type: "recurring" (default weekly schedule) | "one_shot" (run immediately once)
  workflowRunType: 'recurring' | 'one_shot';

  // Dirty state: true when there are unsaved changes since last save/load
  isDirty: boolean;
  markClean: () => void;
  
  // 🆕 Dynamic node types (injected from registry)
  nodeTypesRegistry: any[];
  setNodeTypesRegistry: (nodeTypes: any[]) => void;
  
  // Actions
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  addNode: (type: string, position: { x: number; y: number }) => boolean | string;
  updateNodeData: (nodeId: string, data: any) => void;
  deleteNode: (nodeId: string) => void;
  setSelectedNodeId: (id: string | null) => void;
  
  // Workflow metadata
  setWorkflowName: (name: string) => void;
  setWorkflowDescription: (description: string) => void;
  setWorkflowRunType: (type: 'recurring' | 'one_shot') => void;
  
  // Load/Save
  loadWorkflow: (workflow: Workflow) => void;
  getWorkflow: () => Workflow;
  clearWorkflow: () => void;

  // Undo/Redo
  _undoStack: HistorySnapshot[];
  _redoStack: HistorySnapshot[];
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  pushSnapshot: () => void;

  // Validation
  invalidNodeIds: Set<string>;
  setInvalidNodeIds: (ids: Set<string>) => void;

  // Copy/Paste
  _clipboardNode: Node | null;
  copyNode: (nodeId: string) => void;
  pasteNode: (position?: { x: number; y: number }) => void;
  duplicateNode: (nodeId: string) => void;
}

const MAX_UNDO_HISTORY = 50;

const createZoneNode = (): Node => ({
  id: DATA_ZONE_ID,
  type: 'data_zone',
  position: { ...DATA_ZONE_POSITION },
  data: { label: 'Data Injection', isZone: true },
  selectable: false,
  draggable: false,
  deletable: false,
  style: { zIndex: -1 },
});

const ensureZoneNode = (nodes: Node[]): Node[] => {
  if (nodes.some((n) => n.id === DATA_ZONE_ID)) return nodes;
  return [createZoneNode(), ...nodes];
};

const createDefaultAccountSelector = (): Node => {
  const nodeType = NODE_TYPES.find((nt) => nt.type === 'account_selector');
  const slot = ZONE_SLOTS.find((s) => s.type === 'account_selector');
  return {
    id: `node-${Date.now()}`,
    type: 'account_selector',
    position: {
      x: DATA_ZONE_POSITION.x + (slot?.x || 40),
      y: DATA_ZONE_POSITION.y + (slot?.y || 55),
    },
    data: {
      label: nodeType?.label || 'Select Accounts',
      type: 'account_selector',
      account_selector_config: nodeType?.defaultConfig || {},
    },
  };
};

export const useWorkflowStore = create<WorkflowStore>((set, get) => {
  const takeSnapshot = (): HistorySnapshot => {
    const { nodes, edges, workflowName, workflowDescription } = get();
    return {
      nodes: structuredClone(nodes),
      edges: structuredClone(edges),
      workflowName,
      workflowDescription,
    };
  };

  const pushToUndo = () => {
    const snap = takeSnapshot();
    const stack = [...get()._undoStack, snap];
    if (stack.length > MAX_UNDO_HISTORY) stack.shift();
    set({ _undoStack: stack, _redoStack: [], canUndo: true, canRedo: false });
  };

  return {
  // Initial state
  nodes: [createZoneNode(), createDefaultAccountSelector()],
  edges: [],
  selectedNodeId: null,
  workflowName: 'Untitled Workflow',
  workflowDescription: '',
  workflowRunType: 'recurring' as 'recurring' | 'one_shot',
  isDirty: false,
  nodeTypesRegistry: [], // 🆕 Empty initially, populated from registry
  _undoStack: [],
  _redoStack: [],
  canUndo: false,
  canRedo: false,
  invalidNodeIds: new Set<string>(),
  _clipboardNode: null,
  
  // Dirty state management
  markClean: () => set({ isDirty: false }),

  // Validation
  setInvalidNodeIds: (ids) => set({ invalidNodeIds: ids }),

  // 🆕 Set registry node types
  setNodeTypesRegistry: (nodeTypes) => set({ nodeTypesRegistry: nodeTypes }),
  
  // Canvas actions
  setNodes: (nodes) => set({ nodes }),
  setEdges: (edges) => set({ edges }),
  
  onNodesChange: (changes) => {
    // Mark dirty only on structural changes (not selection, dimensions, etc.)
    const DIRTY_TYPES = new Set(['add', 'remove', 'position', 'reset']);
    const hasDirtyChange = changes.some((c) => DIRTY_TYPES.has(c.type));
    set({
      nodes: applyNodeChanges(changes, get().nodes),
      ...(hasDirtyChange ? { isDirty: true } : {}),
    });
  },
  
  onEdgesChange: (changes) => {
    const DIRTY_TYPES = new Set(['add', 'remove', 'reset']);
    const hasDirtyChange = changes.some((c) => DIRTY_TYPES.has(c.type));
    set({
      edges: applyEdgeChanges(changes, get().edges),
      ...(hasDirtyChange ? { isDirty: true } : {}),
    });
  },
  
  onConnect: (connection) => {
    pushToUndo();
    // Standard edge connection - no special validation for Brand Info
    // Brand Info is selected via dropdown in node config panels
    set({
      edges: addEdge(connection, get().edges),
      isDirty: true,
    });
  },
  
  addNode: (type, position) => {
    // Guard: only one account_selector node is allowed per workflow
    if (type === 'account_selector' && get().nodes.some((n) => n.type === 'account_selector')) {
      return false;
    }

    // Fanpage API nodes use Page Access Token — they do NOT require a personal account_selector
    const FANPAGE_ONLY_NODE_TYPES = [
      'post_to_fanpage', 'fanpage_comment_on_post', 'fanpage_reply_to_comments',
      'fanpage_like_comments', 'fanpage_moderate_comments', 'fanpage_private_reply',
      'facebook_read_insight',
    ];

    // Guard: require account_selector before adding any other node (skip for fanpage-only nodes)
    if (
      type !== 'account_selector' &&
      !FANPAGE_ONLY_NODE_TYPES.includes(type) &&
      !get().nodes.some((n) => n.type === 'account_selector')
    ) {
      return 'need_account_selector';
    }

    pushToUndo();
    // 🆕 Try registry first, fallback to hardcoded
    const nodeTypes = get().nodeTypesRegistry.length > 0 ? get().nodeTypesRegistry : NODE_TYPES;
    const nodeType = nodeTypes.find((nt: any) => nt.type === type);
    const defaultConfig = nodeType?.defaultConfig || {};

    // Create node with proper config structure based on type
    const configKey = `${type}_config`;

    // Auto-position data nodes at their DataZone slot
    let finalPosition = position;
    if (DATA_NODE_TYPES.includes(type)) {
      const zoneNode = get().nodes.find((n) => n.id === DATA_ZONE_ID);
      const slot = ZONE_SLOTS.find((s) => s.type === type);
      if (zoneNode && slot) {
        finalPosition = {
          x: zoneNode.position.x + slot.x,
          y: zoneNode.position.y + slot.y,
        };
      }
    }

    const newNode: Node = {
      id: `node-${Date.now()}`,
      type: type,
      position: finalPosition,
      data: {
        label: nodeType?.label || type,
        type,
        [configKey]: defaultConfig,
      },
    };

    set({ nodes: [...get().nodes, newNode], isDirty: true });
    return true;
  },
  
  updateNodeData: (nodeId, data) => {
    set({
      nodes: get().nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: (() => {
                const mergedData: any = { ...node.data, ...data };
                Object.keys(data || {}).forEach((key) => {
                  if (
                    key.endsWith('_config') &&
                    typeof (node.data as any)[key] === 'object' &&
                    (node.data as any)[key] !== null &&
                    typeof (data as any)[key] === 'object' &&
                    (data as any)[key] !== null
                  ) {
                    mergedData[key] = {
                      ...(node.data as any)[key],
                      ...(data as any)[key],
                    };
                  }
                });
                return mergedData;
              })(),
            }
          : node
      ),
      isDirty: true,
    });
  },
  
  deleteNode: (nodeId) => {
    if (nodeId === DATA_ZONE_ID) return; // protect zone
    // Prevent deleting the last account_selector node
    const targetNode = get().nodes.find((n) => n.id === nodeId);
    if (targetNode?.type === 'account_selector') {
      const accountSelectorCount = get().nodes.filter((n) => n.type === 'account_selector').length;
      if (accountSelectorCount <= 1) return;
    }
    pushToUndo();
    set({
      nodes: get().nodes.filter((node) => node.id !== nodeId),
      edges: get().edges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      ),
      isDirty: true,
    });
  },
  
  setSelectedNodeId: (id) => set({ selectedNodeId: id }),
  
  // Workflow metadata
  setWorkflowName: (name) => {
    set({ workflowName: name, isDirty: true });
  },
  setWorkflowDescription: (description) => {
    set({ workflowDescription: description, isDirty: true });
  },
  setWorkflowRunType: (type) => {
    set({ workflowRunType: type, isDirty: true });
  },
  
  // Load workflow
  loadWorkflow: (workflow) => {
    
    const nodes = workflow.nodes.map((node) => ({
      id: node.id,
      type: node.type, // Use node.type from backend (SOURCE OF TRUTH)
      position: node.position,
      data: {
        label: ((node.data as any).label || 'Untitled') as string,
        ...node.data, // Spread all other data fields
      },
    }));
    
    const edges = workflow.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
    }));
    
    set({
      nodes: ensureZoneNode(nodes),
      edges,
      workflowName: workflow.name,
      workflowDescription: workflow.description,
      workflowRunType: ((workflow.config as any)?.run_type as 'recurring' | 'one_shot') || 'recurring',
      isDirty: false, // freshly loaded — no unsaved changes
      _undoStack: [],
      _redoStack: [],
      canUndo: false,
      canRedo: false,
    });
  },
  
  // Get workflow for saving
  getWorkflow: () => {
    const state = get();

    // Read masterTimeline from account_selector node for weekdays/frequency/timezone
    const accountNode = state.nodes.find((n) => n.type === 'account_selector');
    const acctCfg = (accountNode?.data as any)?.account_selector_config
      || (accountNode?.data as any)?.account_selector
      || (accountNode?.data as any)?.config
      || {};
    const mt = acctCfg.masterTimeline || {};

    return {
      name: state.workflowName,
      description: state.workflowDescription,
      nodes: state.nodes.filter((n) => n.id !== DATA_ZONE_ID).map((node) => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          label: ((node.data as any).label || 'Untitled') as string,
          ...node.data,
        },
      })),
      edges: state.edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
      })),
      config: {
        run_type: state.workflowRunType || 'recurring',
        global_delay: {
          min_seconds: 30,
          max_seconds: 60,
        },
        execution_mode: 'sequential' as const,
        tasks_per_week: mt.baseFrequency || 3,
        weekdays: mt.weekdays || [1, 2, 3, 4, 5, 6, 0],
        timezone: mt.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Ho_Chi_Minh',
      },
    };
  },
  
  clearWorkflow: () => {
    pushToUndo();
    set({
      nodes: [createZoneNode(), createDefaultAccountSelector()],
      edges: [],
      selectedNodeId: null,
      workflowName: 'Untitled Workflow',
      workflowDescription: '',
      workflowRunType: 'recurring',
      isDirty: false, // cleared — no unsaved changes
      _undoStack: [],
      _redoStack: [],
      canUndo: false,
      canRedo: false,
    });
  },

  // Undo/Redo actions
  undo: () => {
    const { _undoStack, _redoStack } = get();
    if (_undoStack.length === 0) return;

    const current = takeSnapshot();
    const prev = _undoStack[_undoStack.length - 1];
    const newUndoStack = _undoStack.slice(0, -1);

    set({
      nodes: prev.nodes,
      edges: prev.edges,
      workflowName: prev.workflowName,
      workflowDescription: prev.workflowDescription,
      _undoStack: newUndoStack,
      _redoStack: [..._redoStack, current],
      canUndo: newUndoStack.length > 0,
      canRedo: true,
      isDirty: true,
    });
  },

  redo: () => {
    const { _undoStack, _redoStack } = get();
    if (_redoStack.length === 0) return;

    const current = takeSnapshot();
    const next = _redoStack[_redoStack.length - 1];
    const newRedoStack = _redoStack.slice(0, -1);

    set({
      nodes: next.nodes,
      edges: next.edges,
      workflowName: next.workflowName,
      workflowDescription: next.workflowDescription,
      _undoStack: [..._undoStack, current],
      _redoStack: newRedoStack,
      canUndo: true,
      canRedo: newRedoStack.length > 0,
      isDirty: true,
    });
  },

  pushSnapshot: () => {
    pushToUndo();
  },

  // Copy/Paste
  copyNode: (nodeId) => {
    const node = get().nodes.find((n) => n.id === nodeId);
    if (!node || node.id === DATA_ZONE_ID) return;
    set({ _clipboardNode: structuredClone(node) });
  },

  pasteNode: (position) => {
    const clipboard = get()._clipboardNode;
    if (!clipboard) return;

    const type = clipboard.type || '';

    // Guard: don't allow pasting data zone nodes or duplicate account_selector
    if (type === 'account_selector' && get().nodes.some((n) => n.type === 'account_selector')) return;
    if (DATA_NODE_TYPES.includes(type)) return;

    pushToUndo();

    const offset = 50;
    const newNode: Node = {
      ...structuredClone(clipboard),
      id: `node-${Date.now()}`,
      position: position || {
        x: clipboard.position.x + offset,
        y: clipboard.position.y + offset,
      },
      selected: false,
    };

    set({
      nodes: [...get().nodes, newNode],
      isDirty: true,
      selectedNodeId: newNode.id,
    });
  },

  duplicateNode: (nodeId) => {
    const node = get().nodes.find((n) => n.id === nodeId);
    if (!node || node.id === DATA_ZONE_ID) return;

    const type = node.type || '';
    if (type === 'account_selector' && get().nodes.some((n) => n.type === 'account_selector')) return;
    if (DATA_NODE_TYPES.includes(type)) return;

    pushToUndo();

    const offset = 50;
    const newNode: Node = {
      ...structuredClone(node),
      id: `node-${Date.now()}`,
      position: {
        x: node.position.x + offset,
        y: node.position.y + offset,
      },
      selected: false,
    };

    set({
      nodes: [...get().nodes, newNode],
      isDirty: true,
      selectedNodeId: newNode.id,
    });
  },
  };
});
